import React, { ComponentType, FC, HTMLAttributes, ReactNode, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { PersonTypes } from '@frontend/api-person';
import { useCallInsightController } from '@frontend/call-insight-panel';
import {
  canUseCallIntelPremiumFeatures,
  callStatusUtils,
  CI_DATE_FORMATS,
  generateTrackingId,
  PrivateField,
  useCallIntelDefaultsShallowStore,
} from '@frontend/call-intel-core';
import { useComponentEngagementShallowStore } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Icon } from '@frontend/icons';
import { OnboardingHotspot, OnboardingHotspotWithTooltip, HOTSPOT_TARGET } from '@frontend/product-onboarding';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Heading, SkeletonLoader, Text, TextLink } from '@frontend/design-system';
import { TRACKING_BASE } from '../constants';
import { useCheckPatientInsightEligibility } from '../hooks/use-check-patient-insight-eligibility.js';
import { usePatientInsightsRequests } from '../hooks/use-patient-insights-requests.js';
import { useCallIntelPatientInsights } from '../providers/patient-insight.provider';
import { PersonInfoStoreProvider, usePersonInfoContext } from '../providers/person-info-store.provider';
import type { CallInsightFilter, PatientCallTrendsTabType } from '../types';
import { CategoryChart } from './charts/category-chart';
import { SentimentChart } from './charts/sentiment-chart';
import IntelCard from './intel-card.js';
import PatientCallTrends from './patient-call-trends';
import { PatientInsightTeaser } from './patient-insight-teasers/patient-insight-teaser';
import { TeaserCard } from './patient-insight-teasers/teaser-card';

export interface PatientInsightsActions {
  /** when the “primary” header button is clicked (e.g. “View History”) */
  onPrimaryActionClick?: () => void;
  /** when the user clicks on a chart slice/bar to apply a filter */
  onChartClick?: () => void;
  /** when you want to apply or clear a filter */
  onFilterChange?: (filter: CallInsightFilter) => void;
}

interface BasePatientInsightProps {
  title?: string;
  primaryActionLabel?: string;
  actions?: PatientInsightsActions;
  isDemoView?: boolean;
  hasCallIntelPremiumFeatures?: boolean;
  trackComponentInteraction?: {
    componentId: string;
    enable: boolean;
  };
}

interface PatientInsightsProps extends BasePatientInsightProps {
  locationId?: string;
  onViewDemo?: (config: { action: ReactNode }) => void;
  person?: PersonTypes.Person;
  wrapper?: ComponentType<{ children: ReactNode }>;
}

interface PatientInsightsComponentProps extends BasePatientInsightProps, HTMLAttributes<HTMLDivElement> {}

interface InsightsHeaderProps extends BasePatientInsightProps {
  showAction: boolean;
}

const InsightsLoader = () => {
  return (
    <div css={styles.loaderWrapper}>
      <SkeletonLoader height={136} />
      <SkeletonLoader height={20} />
    </div>
  );
};

const InsightsHeader = ({
  actions = {},
  title,
  showAction,
  isDemoView,
  primaryActionLabel,
  hasCallIntelPremiumFeatures,
}: InsightsHeaderProps) => {
  const { t } = useTranslation('callIntelInsights');
  const { onPrimaryActionClick } = actions;

  const showCta = showAction && onPrimaryActionClick;

  return (
    <div css={styles.actionWrapper}>
      <div css={styles.headerTitle}>
        {isDemoView && <Icon name={'premium-badge'} size={24} />}
        <Heading level={3}>
          {title ?? (hasCallIntelPremiumFeatures ? t('Call Intelligence') : t('Call History'))}
        </Heading>
      </div>
      {showCta && (
        <OnboardingHotspot target={HOTSPOT_TARGET.PATIENT_PROFILE_VIEW_HISTORY_BUTTON} offset={{ top: 0, right: -6 }}>
          <TextLink
            size='medium'
            weight='bold'
            onClick={onPrimaryActionClick}
            trackingId={generateTrackingId({
              ...TRACKING_BASE,
              context: 'primary-btn-click',
            })}
          >
            {primaryActionLabel ?? t('View History')}
          </TextLink>
        </OnboardingHotspot>
      )}
    </div>
  );
};

const PatientInsightsComponent: FC<PatientInsightsComponentProps> = ({
  actions = {},
  isDemoView,
  trackComponentInteraction,
  hasCallIntelPremiumFeatures,
  ...rest
}) => {
  const { t } = useTranslation('callIntelInsights');
  const { patientCapitalized: patientCapitalizedToken } = useTranslationTokens();
  const { personInfo } = usePersonInfoContext();
  const { selectedLocationIds } = useAppScopeStore();
  const selectedLocationId = selectedLocationIds?.[0] || '';

  const { activeTab, setActiveTab, showTrends, setShowTrends, dateRange } = useCallIntelPatientInsights();
  const { lastCallDetail, isLastCallLoading, callTrends, isCallTrendsLoading } = usePatientInsightsRequests({
    dateRange,
    personId: personInfo.PersonID,
    showTrends,
    isDemoView,
  });

  const { trackInteraction } = useComponentEngagementShallowStore('trackInteraction');

  const { open: openCallInsightPanel, setIsDemoAccount } = useCallInsightController({
    source: 'patientInsightsContactPanel',
  });
  const { dataLabels } = useCallIntelDefaultsShallowStore('dataLabels');
  const { onChartClick, onFilterChange } = actions;

  const { call, summary } = lastCallDetail ?? {};

  const isEmpty = !call;

  const { isSkippedCall, isProcessingCall, isAnalyzed, isPrivateCall } = callStatusUtils.getCallFlags(call);

  const isTrendsDataLimited = false; /* true if < 1 year of CI data */ // TODO: Replace with actual value from api

  const showCallTrends = hasCallIntelPremiumFeatures || isDemoView;

  const registerInteraction = useCallback(() => {
    if (trackComponentInteraction?.enable) {
      trackInteraction({
        componentId: trackComponentInteraction.componentId,
      });
    }
  }, [trackInteraction, trackComponentInteraction]);

  const handleChartClick = useCallback(
    (filter: CallInsightFilter) => {
      onFilterChange?.(filter);
      onChartClick?.();
      registerInteraction();
    },
    [onFilterChange, onChartClick, registerInteraction]
  );

  const handleOpenCallTakeaway = () => {
    if (call) {
      const isDemo = personInfo.PersonID.startsWith('demo');
      if (isDemo) {
        call.locationId ||= selectedLocationId; // TODO: Remove this selectedLocationId fallback when BE fixes the issue
        call.person ||= {
          id: personInfo.PersonID,
          firstName: personInfo.FirstName,
          lastName: personInfo.LastName,
        };
      }
      setIsDemoAccount(isDemo);
      openCallInsightPanel({
        channelId: call.id ?? '',
        locationId: call.locationId ?? '',
        includeCallIntelligence: false,
        callIntelData: call,
      });
      registerInteraction();
    }
  };

  const renderCallContent = useMemo(() => {
    if (isProcessingCall) {
      return <IntelCard.ProcessingCall />;
    }

    if (!isAnalyzed && !isPrivateCall) {
      return <IntelCard.NotAnalyzed />;
    }

    if (isPrivateCall) {
      return <PrivateField isCompact={false} />;
    }

    return (
      <>
        {hasCallIntelPremiumFeatures && (
          <IntelCard.Attributes>
            <IntelCard.Sentiment sentiment={call?.callSentiment?.sentiment} />
            <IntelCard.Category category={call?.categories?.[0]} />
            <IntelCard.Tasks count={call?.taskCount} />
          </IntelCard.Attributes>
        )}
        <IntelCard.Summary>{summary}</IntelCard.Summary>
      </>
    );
  }, [
    call?.callSentiment?.sentiment,
    call?.categories,
    call?.taskCount,
    isAnalyzed,
    isPrivateCall,
    isProcessingCall,
    summary,
    hasCallIntelPremiumFeatures,
  ]);

  const formattedDate = useMemo(() => {
    return call?.startTime ? dayjs(call.startTime).format(CI_DATE_FORMATS.DATE) : '';
  }, [call?.startTime]);

  return (
    <div css={[styles.containerStyles({ isLoading: isLastCallLoading })]} {...rest} data-testid='patient-insights'>
      <InsightsHeader actions={actions} showAction={!isEmpty && !isLastCallLoading} isDemoView={isDemoView} {...rest} />

      {isLastCallLoading && <InsightsLoader />}

      {!isLastCallLoading && isEmpty && <IntelCard.EmptyState />}

      {!isLastCallLoading && !isEmpty && (
        <>
          <OnboardingHotspotWithTooltip
            target={HOTSPOT_TARGET.PATIENT_PROFILE_LAST_CALL_CARD}
            position='top-left'
            hotspotOffset={{ top: -4, left: -4 }}
          >
            <IntelCard
              onClick={handleOpenCallTakeaway}
              trackingId={generateTrackingId({
                ...TRACKING_BASE,
                component: isDemoView ? 'patient-insights-demo' : 'patient-insights',
                context: 'last-call-card-click',
              })}
            >
              <IntelCard.Header
                title={t('Last Call')}
                isAnalyzed={isAnalyzed}
                isProcessingCall={isProcessingCall}
                leftIcon={
                  <Icon
                    name={call?.direction === 'DIRECTION_OUTBOUND' ? 'phone-outgoing-small' : 'phone-incoming-small'}
                  />
                }
                rightIcon={
                  isPrivateCall ? undefined : !isSkippedCall ? (
                    <Icon name='error-badge-small' color='error' />
                  ) : (
                    <Icon name='help-filled-small' primaryColor='disabled' secondaryColor='subdued' />
                  )
                }
                value={formattedDate}
                tooltipContent={
                  isSkippedCall
                    ? t(
                        'Calls are not analyzed if they have a single participant, very short audio, or audio exceeding an hour in length.'
                      )
                    : t('We are unable to transcribe and analyze this call. ')
                }
              />
              {renderCallContent}
            </IntelCard>
          </OnboardingHotspotWithTooltip>
          {showCallTrends && (
            <PatientCallTrends<PatientCallTrendsTabType>
              activeTab={activeTab}
              defaultTab='sentiment-tab'
              isLoading={isCallTrendsLoading}
              isOpen={showTrends}
              onTabChange={setActiveTab}
              onToggle={(open) => {
                setShowTrends(open);
                registerInteraction();
              }}
            >
              <PatientCallTrends.Header
                duration={t('Last 12 Months')}
                isLimitedData={isTrendsDataLimited}
                title={t('{{patientCapitalizedToken}} Call Trends', { patientCapitalizedToken })}
                tooltipContent={t(
                  '{{patientCapitalizedToken}} Call Trends are limited due to this location having less than a year of Call Intelligence data.',
                  { patientCapitalizedToken }
                )}
                trackingId={generateTrackingId({
                  ...TRACKING_BASE,
                  context: 'call-trends-accordion-click',
                })}
              />

              <PatientCallTrends.Content>
                <PatientCallTrends.Navigation fullWidth>
                  <PatientCallTrends.Item
                    id='sentiment-tab'
                    trackingId={generateTrackingId({
                      ...TRACKING_BASE,
                      context: 'call-trends-sentiment-tab',
                    })}
                  >
                    {t('Customer Sentiment')}
                  </PatientCallTrends.Item>
                  <PatientCallTrends.Item
                    id='category-tab'
                    trackingId={generateTrackingId({
                      ...TRACKING_BASE,
                      context: 'call-trends-categories-tab',
                    })}
                  >
                    {t('Call Categories')}
                  </PatientCallTrends.Item>
                </PatientCallTrends.Navigation>

                <PatientCallTrends.View controller='sentiment-tab'>
                  <SentimentChart
                    sentiments={callTrends?.sentiments ?? {}}
                    isLoading={isCallTrendsLoading}
                    onSliceClick={
                      isDemoView
                        ? undefined
                        : ({ name }) =>
                            handleChartClick({
                              filterType: 'sentiment',
                              key: name,
                              label: dataLabels.sentiments?.[name] ?? '',
                            })
                    }
                  />
                </PatientCallTrends.View>
                <PatientCallTrends.View controller='category-tab'>
                  <CategoryChart
                    categories={callTrends?.categories ?? {}}
                    isLoading={isCallTrendsLoading}
                    onBarClick={
                      isDemoView
                        ? undefined
                        : ({ barId }) =>
                            handleChartClick({
                              filterType: 'category',
                              key: barId,
                              label: dataLabels.categories?.[barId] ?? '',
                            })
                    }
                  />
                </PatientCallTrends.View>
              </PatientCallTrends.Content>
            </PatientCallTrends>
          )}

          {!isLastCallLoading && (
            <Text color='subdued' css={styles.footer} size='small'>
              {t('This AI-generated data may not always be accurate. Please review the Call Insights carefully.')}
            </Text>
          )}
        </>
      )}
    </div>
  );
};

export const PatientInsights = React.memo(
  ({ person, locationId, wrapper: Wrapper, isDemoView, onViewDemo, ...rest }: PatientInsightsProps) => {
    const { showPatientInsight, showPatientInsightTeaser, currentPlan } = useCheckPatientInsightEligibility({
      locationId,
    });

    const hasCallIntelPremiumFeatures = canUseCallIntelPremiumFeatures(currentPlan);

    const handleViewDemo = useCallback(() => {
      onViewDemo?.({
        action: (
          <TeaserCard.UpgradeAction
            locationId={locationId}
            clickSource='demo-contact-panel'
            trackingOptions={{ component: 'panel-header', page: 'patient-insight-demo' }}
          />
        ),
      });
    }, [onViewDemo, locationId]);

    if (!person || !showPatientInsight) return null;

    const patientInsightRender =
      showPatientInsightTeaser && !isDemoView ? (
        <PatientInsightTeaser locationId={locationId} onViewDemo={handleViewDemo} />
      ) : (
        <PersonInfoStoreProvider personInfo={person}>
          <PatientInsightsComponent
            isDemoView={isDemoView}
            hasCallIntelPremiumFeatures={hasCallIntelPremiumFeatures}
            {...rest}
          />
        </PersonInfoStoreProvider>
      );

    return Wrapper ? <Wrapper>{patientInsightRender}</Wrapper> : patientInsightRender;
  }
);

PatientInsights.displayName = 'PatientInsights';

const styles = {
  containerStyles: ({ isLoading }: { isLoading: boolean }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(2)};
    gap: ${theme.spacing(2)};
    border-radius: ${theme.borderRadius.medium};
    background-color: ${theme.colors.white};
    min-height: ${isLoading ? 250 : 0}px;
    max-height: ${isLoading ? 250 : 600}px;
    height: auto;
    transition: max-height 0.5s ease-in-out, min-height 0.5s ease-in-out;
    overflow: hidden;
  `,
  headerTitle: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  actionWrapper: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  footer: css`
    font-style: italic;
    border-top: 1px solid ${theme.colors.neutral20};
    padding-top: ${theme.spacing(1)};
  `,
  loaderWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
};
